import { useState } from 'react';
import { Download, FileUp, ListOrdered, Music, Pencil, Plus, Power, PowerOff, Search, SlidersHorizontal, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import type { CourseAdminView, CoursePagination } from '@/lib/types';
import {
  COURSE_COLUMNS,
  COURSE_PAGE_SIZE_OPTIONS,
  courseTotalPages,
  normalizeCoursePageSize,
  resolveCourseColumns,
  toggleCourseColumn,
  type CourseCatalogFilterState,
  type CourseSortBy,
  type CourseStatusFilter,
} from '@/lib/course-catalog-filters';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';
import { formatDateTime } from '@/lib/format';

const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const INPUT =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

/**
 * Issue #221 / #345 — 课程目录工作区。Issue #345 起分页 / 搜索 / 排序全部走后端
 * GET /admin/courses（真实分页 { items, pagination }），本组件只负责展示当前页与筛选控件；
 * 筛选状态由父路由持有并驱动查询。列隐藏保持纯展示层能力。CRUD 仍由父组件的真实接口
 * + 弹窗承载（这里只暴露行内操作回调），绝不前端假状态 / 本地分页。
 */
export function CourseCatalog({
  courses,
  pagination,
  filter,
  onFilterChange,
  loading,
  error,
  onRetry,
  onCreate,
  onEdit,
  onAudio,
  onImport,
  onExport,
  onSegments,
  onToggleStatus,
  onDelete,
  statusPendingId,
  exportPendingId,
}: {
  courses: CourseAdminView[];
  pagination: CoursePagination | undefined;
  filter: CourseCatalogFilterState;
  onFilterChange: (patch: Partial<CourseCatalogFilterState>) => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onCreate: () => void;
  onEdit: (course: CourseAdminView) => void;
  onAudio: (course: CourseAdminView) => void;
  onImport: (course: CourseAdminView) => void;
  /** Issue #7：导出课程 Markdown（走真实后端接口下载，非前端拼接）。 */
  onExport: (course: CourseAdminView) => void;
  onSegments: (course: CourseAdminView) => void;
  onToggleStatus: (course: CourseAdminView) => void;
  onDelete: (course: CourseAdminView) => void;
  statusPendingId: string | null;
  /** 正在导出的课程 id（禁用该行导出按钮，避免重复点击）。 */
  exportPendingId: string | null;
}) {
  const [pendingKeyword, setPendingKeyword] = useState(filter.keyword);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    resolveCourseColumns(null),
  );
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);

  const colVisible = (key: string) => visibleColumns.includes(key);

  // 页码 / 总数一律以后端 pagination 为准（旧接口无 pagination 时退化为当前页长度）。
  const total = pagination?.total ?? courses.length;
  const currentPage = pagination?.page ?? filter.page;
  const totalPages = pagination?.totalPages ?? courseTotalPages(total, filter.pageSize);

  const patch = (next: Partial<CourseCatalogFilterState>) =>
    // 任意筛选/排序/页大小变更都回到第 1 页，避免停留在越界页；翻页显式带 page。
    onFilterChange({ ...next, page: next.page ?? 1 });

  return (
    <Card
      title="课程目录"
      action={
        <button type="button" className={PRIMARY_BTN} onClick={onCreate}>
          <Plus className="h-4 w-4" />
          新增课程
        </button>
      }
    >
      {/* 高级搜索栏 */}
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>关键词（课程名 / 编码）</span>
          <div className="flex items-center gap-1">
            <input
              className={clsx(INPUT, 'w-56')}
              value={pendingKeyword}
              placeholder="搜索课程…"
              onChange={(e) => setPendingKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') patch({ keyword: pendingKeyword });
              }}
              data-testid="course-keyword"
            />
            <button
              type="button"
              className={SECONDARY_BTN}
              onClick={() => patch({ keyword: pendingKeyword })}
            >
              <Search className="h-3.5 w-3.5" />
              查询
            </button>
          </div>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>状态</span>
          <select
            className={INPUT}
            value={filter.status}
            onChange={(e) => patch({ status: e.target.value as CourseStatusFilter })}
          >
            <option value="ALL">全部</option>
            <option value="ENABLED">启用</option>
            <option value="DISABLED">停用</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>排序</span>
          <select
            className={INPUT}
            value={filter.sortBy}
            onChange={(e) => patch({ sortBy: e.target.value as CourseSortBy })}
          >
            <option value="sortOrder">排序值</option>
            <option value="name">课程名称</option>
            <option value="questionCount">题量</option>
            <option value="updatedAt">更新时间</option>
          </select>
        </label>
        <button
          type="button"
          className={SECONDARY_BTN}
          onClick={() =>
            patch({ sortOrder: filter.sortOrder === 'asc' ? 'desc' : 'asc' })
          }
        >
          {filter.sortOrder === 'asc' ? '升序 ↑' : '降序 ↓'}
        </button>
        <button
          type="button"
          className={clsx(SECONDARY_BTN, 'ml-auto')}
          onClick={() => setColumnPanelOpen((v) => !v)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          列显示
        </button>
      </div>

      {columnPanelOpen ? (
        <div className="mb-3 flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          {COURSE_COLUMNS.map((col) => (
            <label
              key={col.key}
              className={clsx(
                'flex items-center gap-1.5 text-xs',
                col.hideable ? 'text-slate-600' : 'text-slate-400',
              )}
            >
              <input
                type="checkbox"
                checked={colVisible(col.key)}
                disabled={!col.hideable}
                onChange={() => setVisibleColumns((prev) => toggleCourseColumn(prev, col.key))}
              />
              {col.label}
            </label>
          ))}
        </div>
      ) : null}

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState title="课程接口加载失败" description={error} retry={onRetry} />
      ) : total === 0 ? (
        <EmptyState
          title={filter.keyword || filter.status !== 'ALL' ? '没有匹配的课程' : '尚未创建课程'}
          description="点击右上角「新增课程」创建课程，然后选择课程导入一问一答知识库。"
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  {colVisible('name') ? <th className="py-2 pr-3">课程名称</th> : null}
                  {colVisible('code') ? <th className="py-2 pr-3">编码</th> : null}
                  {colVisible('questionCount') ? <th className="py-2 pr-3">题量</th> : null}
                  {colVisible('segments') ? <th className="py-2 pr-3">分段</th> : null}
                  {colVisible('status') ? <th className="py-2 pr-3">状态</th> : null}
                  {colVisible('sortOrder') ? <th className="py-2 pr-3">排序值</th> : null}
                  {colVisible('updatedAt') ? <th className="py-2 pr-3">更新时间</th> : null}
                  <th className="py-2 pr-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => {
                  const enabled = course.status === 'ENABLED';
                  return (
                    <tr
                      key={course.id}
                      className="border-b border-slate-100"
                      data-testid={`course-row-${course.id}`}
                    >
                      {colVisible('name') ? (
                        <td className="py-2 pr-3 font-medium text-slate-900">
                          {course.name}
                          {course.description ? (
                            <div className="text-[11px] font-normal text-slate-400">
                              {course.description}
                            </div>
                          ) : null}
                        </td>
                      ) : null}
                      {colVisible('code') ? (
                        <td className="py-2 pr-3 text-slate-500">{course.code}</td>
                      ) : null}
                      {colVisible('questionCount') ? (
                        <td className="py-2 pr-3 text-slate-600">{course.questionCount}</td>
                      ) : null}
                      {colVisible('segments') ? (
                        <td className="py-2 pr-3">
                          {/* Issue #380：分段配置状态。segmentCount 权威计数来自后端。 */}
                          <span
                            className={clsx(
                              'rounded-full px-2 py-0.5 text-[10px] font-medium',
                              course.segmentCount > 0
                                ? 'bg-brand-100 text-brand-700'
                                : 'bg-slate-200 text-slate-600',
                            )}
                            data-testid={`course-segment-status-${course.id}`}
                          >
                            {course.segmentCount > 0
                              ? `已配置 ${course.segmentCount} 个批次`
                              : '未配置'}
                          </span>
                        </td>
                      ) : null}
                      {colVisible('status') ? (
                        <td className="py-2 pr-3">
                          <span
                            className={clsx(
                              'rounded-full px-2 py-0.5 text-[10px] font-medium',
                              enabled
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-200 text-slate-600',
                            )}
                          >
                            {enabled ? '启用' : '停用'}
                          </span>
                        </td>
                      ) : null}
                      {colVisible('sortOrder') ? (
                        <td className="py-2 pr-3 text-slate-500">{course.sortOrder}</td>
                      ) : null}
                      {colVisible('updatedAt') ? (
                        <td className="py-2 pr-3 text-slate-500">
                          {formatDateTime(course.updatedAt)}
                        </td>
                      ) : null}
                      <td className="py-2 pr-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Issue #316：课程音频导入入口，位于「导入」之前。已上传时图标着主题蓝高亮。 */}
                          <button
                            type="button"
                            className={clsx(SECONDARY_BTN, course.audio ? 'text-brand-600' : null)}
                            onClick={() => onAudio(course)}
                            title={course.audio ? '已上传课程音频，点击替换' : '上传课程音频（MP3）'}
                            data-testid={`course-audio-${course.id}`}
                          >
                            <Music className="h-3.5 w-3.5" />
                            音频
                          </button>
                          <button type="button" className={SECONDARY_BTN} onClick={() => onImport(course)}>
                            <FileUp className="h-3.5 w-3.5" />
                            导入
                          </button>
                          {/*
                            Issue #7：导出课程 Markdown。紧邻「导入」，两者互为逆操作——
                            导出的 .md 可被同一课程的导入预检原样解析回来。
                          */}
                          <button
                            type="button"
                            className={SECONDARY_BTN}
                            onClick={() => onExport(course)}
                            disabled={exportPendingId === course.id}
                            title="导出该课程为 Markdown（可再次导入）"
                            data-testid={`course-export-${course.id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                            {exportPendingId === course.id ? '导出中…' : '导出'}
                          </button>
                          {/* Issue #342：分段批次配置入口。 */}
                          <button
                            type="button"
                            className={SECONDARY_BTN}
                            onClick={() => onSegments(course)}
                            data-testid={`course-segments-${course.id}`}
                          >
                            <ListOrdered className="h-3.5 w-3.5" />
                            分段
                          </button>
                          <button type="button" className={SECONDARY_BTN} onClick={() => onEdit(course)}>
                            <Pencil className="h-3.5 w-3.5" />
                            编辑
                          </button>
                          <button
                            type="button"
                            className={SECONDARY_BTN}
                            onClick={() => onToggleStatus(course)}
                            disabled={statusPendingId === course.id}
                          >
                            {enabled ? (
                              <>
                                <PowerOff className="h-3.5 w-3.5" />
                                停用
                              </>
                            ) : (
                              <>
                                <Power className="h-3.5 w-3.5" />
                                启用
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className={clsx(SECONDARY_BTN, 'text-rose-600')}
                            onClick={() => onDelete(course)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Issue #345：分页控件——页码 / 总数来自后端 pagination。仅在 total > 20 时显示翻页按钮。 */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span data-testid="course-pagination-summary">
              共 {total} 门课程 · 第 {currentPage}/{totalPages} 页
            </span>
            <div className="flex items-center gap-2">
              <select
                className={INPUT}
                value={filter.pageSize}
                onChange={(e) => patch({ pageSize: normalizeCoursePageSize(Number(e.target.value)) })}
                data-testid="course-page-size"
              >
                {COURSE_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} 条/页
                  </option>
                ))}
              </select>
              {total > filter.pageSize ? (
                <>
                  <button
                    type="button"
                    className={SECONDARY_BTN}
                    disabled={currentPage <= 1}
                    onClick={() => onFilterChange({ page: currentPage - 1 })}
                    data-testid="course-prev-page"
                  >
                    上一页
                  </button>
                  <button
                    type="button"
                    className={SECONDARY_BTN}
                    disabled={currentPage >= totalPages}
                    onClick={() => onFilterChange({ page: currentPage + 1 })}
                    data-testid="course-next-page"
                  >
                    下一页
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
